import { useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

// Curated, project-friendly emoji set (no dependency, no ZWJ/skin-tone
// sequences so values stay short and within the 16-char column).
const EMOJI_GROUPS: { label: string; emojis: string[] }[] = [
  {
    label: 'Work',
    emojis: ['📁', '📂', '🗂️', '📦', '📊', '📈', '📉', '📋', '📌', '📎', '🗃️', '🗄️', '📝', '🧾', '💼', '🗓️'],
  },
  {
    label: 'Build',
    emojis: ['🚀', '🛠️', '⚙️', '🧩', '🔧', '🔩', '🧱', '🏗️', '🪛', '🧰', '🔌', '🧪', '⚗️', '🔬', '🧬', '🛰️'],
  },
  {
    label: 'Tech',
    emojis: ['💻', '🖥️', '📱', '⌨️', '🖱️', '🤖', '📡', '🌐', '☁️', '🗜️', '💾', '💿', '🔋', '🕹️', '📟', '🧮'],
  },
  {
    label: 'Symbols',
    emojis: ['⭐', '🔥', '✨', '⚡', '💎', '🎯', '🏆', '🥇', '🚩', '🏁', '✅', '🧠', '👁️', '🔒', '🔑', '💡'],
  },
  {
    label: 'Nature',
    emojis: ['🌱', '🌳', '🌍', '🌊', '🌈', '☀️', '🌙', '🍀', '🌸', '🔆', '❄️', '🌟', '🪐', '🌋', '🏔️', '🌵'],
  },
  {
    label: 'Misc',
    emojis: ['😀', '😎', '🤓', '🥳', '👍', '👏', '💪', '🙌', '👀', '🎉', '🎨', '🎵', '📣', '🔔', '❤️', '💙'],
  },
];

const ALL_EMOJIS = EMOJI_GROUPS.flatMap((g) => g.emojis);

interface EmojiPickerProps {
  value: string;
  onChange: (emoji: string) => void;
  /** Optional fallback shown in the trigger when nothing is picked. */
  placeholder?: string;
  /** Icon-only square trigger (no label) — for tight spots like a page header. */
  compact?: boolean;
  /** Read-only: show the icon but don't allow opening the picker. */
  disabled?: boolean;
}

export function EmojiPicker({
  value,
  onChange,
  placeholder = 'Pick an icon',
  compact,
  disabled,
}: EmojiPickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim();
    if (!q) return null; // null ⇒ show grouped view
    // Match against the raw emoji (handles paste) — name search would need a map.
    return ALL_EMOJIS.filter((e) => e.includes(q));
  }, [query]);

  function pick(emoji: string) {
    onChange(emoji);
    setOpen(false);
    setQuery('');
  }

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        if (disabled) return;
        setOpen(o);
        if (!o) setQuery('');
      }}
    >
      <PopoverTrigger asChild>
        {compact ? (
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="size-9"
            aria-label={placeholder}
            disabled={disabled}
          >
            <span className="text-lg leading-none">{value || '📄'}</span>
          </Button>
        ) : (
          <Button
            type="button"
            variant="outline"
            className="h-9 w-full justify-start gap-2 font-normal"
            disabled={disabled}
          >
            <span className="text-lg leading-none">{value || '📁'}</span>
            <span className={cn('truncate', !value && 'text-muted-foreground')}>
              {value ? 'Change icon' : placeholder}
            </span>
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-2">
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Type or paste an emoji…"
          className="mb-2 h-8"
        />
        <div className="max-h-56 space-y-3 overflow-y-auto">
          {filtered ? (
            <div className="grid grid-cols-8 gap-0.5">
              {filtered.length === 0 ? (
                <p className="text-muted-foreground col-span-8 px-1 py-2 text-xs">
                  No match — paste any emoji to use it.
                </p>
              ) : (
                filtered.map((e) => <EmojiButton key={e} emoji={e} active={e === value} onPick={pick} />)
              )}
              {query.trim() && !ALL_EMOJIS.includes(query.trim()) ? (
                <EmojiButton emoji={query.trim()} active={query.trim() === value} onPick={pick} />
              ) : null}
            </div>
          ) : (
            EMOJI_GROUPS.map((g) => (
              <div key={g.label}>
                <p className="text-muted-foreground mb-1 px-1 text-xs font-medium">{g.label}</p>
                <div className="grid grid-cols-8 gap-0.5">
                  {g.emojis.map((e) => (
                    <EmojiButton key={e} emoji={e} active={e === value} onPick={pick} />
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
        {value ? (
          <button
            type="button"
            onClick={() => pick('')}
            className="text-muted-foreground hover:text-foreground mt-2 w-full border-t pt-2 text-xs"
          >
            Remove icon
          </button>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}

function EmojiButton({
  emoji,
  active,
  onPick,
}: {
  emoji: string;
  active: boolean;
  onPick: (emoji: string) => void;
}) {
  return (
    <button
      type="button"
      aria-label={`Use ${emoji}`}
      onClick={() => onPick(emoji)}
      className={cn(
        'hover:bg-muted flex size-8 items-center justify-center rounded text-lg',
        active && 'bg-muted ring-primary ring-1',
      )}
    >
      {emoji}
    </button>
  );
}
